import type { AbsenceReason } from '@ukinfo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { ABSENCE_REASON_LABELS, formatShort } from '../lib/format';
import { cn } from './ui';

export interface HistoryEntry {
  id: string;
  personId: string;
  name: string;
  rsvp: 'YES' | 'NO' | 'MAYBE' | 'PENDING';
  reason: AbsenceReason | null;
  note: string | null;
  recordedAt: string;
  source: string;
  actorName: string | null;
  late: boolean;
  beforeReschedule: boolean;
}

interface History {
  canReadReasons: boolean;
  deadline: string | null;
  entries: HistoryEntry[];
}

const RSVP_TEXT: Record<HistoryEntry['rsvp'], string> = { YES: 'zugesagt', NO: 'abgesagt', MAYBE: 'vielleicht', PENDING: 'offen' };
const RSVP_FARBE: Record<HistoryEntry['rsvp'], string> = {
  YES: 'text-emerald-700', NO: 'text-rose-700', MAYBE: 'text-amber-700', PENDING: 'text-slate-500',
};

function herkunft(entry: HistoryEntry): string | null {
  if (entry.actorName) return `eingetragen von ${entry.actorName}`;
  if (entry.source === 'PERSONAL_LINK') return 'über Link';
  if (entry.source === 'SELF') return 'selbst';
  return null;
}

export function useResponseHistory(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['response-history', sessionId],
    queryFn: () => api.get<History>(`/api/sports/sessions/${sessionId}/history`),
    enabled,
    staleTime: 0,
  });
}

/**
 * Verlauf der Rückmeldungen: jede Zu- und Absage mit Zeitpunkt und Herkunft.
 * Mit `personId` nur die einer Person, sonst die letzten Änderungen aller.
 */
export function ResponseHistory({ sessionId, personId, limit }: { sessionId: string; personId?: string; limit?: number }): JSX.Element | null {
  const verlauf = useResponseHistory(sessionId, true);
  const [alle, setAlle] = useState(false);
  if (verlauf.isLoading) return <p className="px-1 py-1 text-xs text-slate-500">Verlauf wird geladen …</p>;
  // Ohne Leserecht auf Rückmeldungen gibt es keinen Verlauf - dann nichts zeigen.
  if (verlauf.error || !verlauf.data) return null;
  const eintraege = verlauf.data.entries.filter((entry) => !personId || entry.personId === personId);
  if (eintraege.length === 0) {
    return <p className="px-1 py-1 text-xs text-slate-500">{personId ? 'Noch keine Rückmeldung gegeben.' : 'Noch keine Rückmeldungen.'}</p>;
  }
  const sichtbar = limit && !alle ? eintraege.slice(0, limit) : eintraege;
  return (
    <div>
      <ol className="space-y-1 text-xs" aria-label={personId ? 'Verlauf der Rückmeldungen' : 'Letzte Änderungen'}>
        {sichtbar.map((entry) => {
          const zusatz = [
            entry.reason ? ABSENCE_REASON_LABELS[entry.reason] : null,
            herkunft(entry),
          ].filter(Boolean).join(' · ');
          return (
            <li key={entry.id} className={cn('flex flex-wrap gap-x-1.5', entry.beforeReschedule && 'opacity-60')}>
              <span className="tabular-nums text-slate-500">{formatShort(entry.recordedAt)}</span>
              {!personId && <span className="font-medium text-slate-800">{entry.name}</span>}
              <span className={cn('font-medium', RSVP_FARBE[entry.rsvp])}>{RSVP_TEXT[entry.rsvp]}</span>
              {zusatz && <span className="text-slate-500">· {zusatz}</span>}
              {entry.note && <span className="text-slate-600">– „{entry.note}“</span>}
              {entry.late && <span className="rounded bg-amber-100 px-1 font-medium text-amber-900">nach Frist</span>}
              {entry.beforeReschedule && <span className="text-slate-500">(vor der Verschiebung)</span>}
            </li>
          );
        })}
      </ol>
      {limit && eintraege.length > limit && (
        <button type="button" className="mt-1 min-h-[32px] text-xs font-medium text-brand-700 hover:underline" onClick={() => setAlle(!alle)}>
          {alle ? 'Weniger anzeigen' : `Alle ${eintraege.length} Änderungen anzeigen`}
        </button>
      )}
    </div>
  );
}
